// FILE: apps/admin/src/pages/Treasury.tsx
// VERSION: 2.0.0
// START_MODULE_CONTRACT
//   PURPOSE: Tabbed Admin Treasury control plane showing GRAM/TON wallet reserves, Telegram Bot Stars 21-day hold breakdown, user liabilities coverage, ledger DRIFT reconciliation, and platform P&L.
//   SCOPE: Treasury component with 4 horizontal tabs (overview, bot-stars, drifts, pnl) and drift reconciliation action.
//   DEPENDS: ../api.js, ../components/Icons.js, ../display-currency.js, ../toast.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   Treasury - Admin Treasury dashboard with 4 KPI cards, Bot Stars 21-day hold tab, DRIFT reconciliation tab, and P&L breakdown
// END_MODULE_MAP

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { api, dt, gram, num, rub } from '../api.js';
import { AdminIcon } from '../components/Icons.js';
import { useDisplayCurrency } from '../display-currency.js';
import { useToast } from '../toast.js';

type TreasuryTab = 'overview' | 'bot-stars' | 'drifts' | 'pnl';

interface HeldStarTxRow {
  id: string;
  stars: number;
  createdAt: string;
  unlocksAt: string;
  fromUsername?: string | null;
  fromLabel?: string | null;
}

interface TreasuryDriftRow {
  userId: string;
  telegramId: number;
  username: string | null;
  displayName?: string | null;
  currency: 'RUB' | 'GRAM';
  storedBalance: number;
  ledgerSum: number;
  driftAmount: number;
}

interface TreasuryResponse {
  tonWallet: {
    address: string | null;
    balanceTon: number;
    reservedTon: number;
    availableTon: number;
    healthy: boolean;
    updatedAt: string;
  };
  botStars?: {
    available: boolean;
    totalStars: number;
    availableToWithdrawStars: number;
    heldStars: number;
    nextUnlockAt: string | null;
    recentHeldTransactions: HeldStarTxRow[];
    error?: string | null;
  };
  fragment: {
    connected: boolean;
    mode: string;
    kycStatus: string;
    accountLabel: string | null;
    lastCheckedAt?: string | null;
  };
  tbank: {
    state: string;
    maskedPhone: string | null;
    lastPollAt?: string | null;
    circuitOpen: boolean;
  };
  liabilities: {
    totalUserBalanceKopecks: number;
    totalUserBalanceMilliGram: number;
    usersWithPositiveBalanceCount: number;
    ledgerDriftUsersCount: number;
  };
  coverage?: {
    rubLiabilitiesKopecks: number;
    gramLiabilitiesMilli: number;
    gramWalletMilli: number;
    gramSurplusOrDeficitMilli: number;
    gramCovered: boolean;
    totalLiabilitiesRubEquivKopecks: number;
    totalLiquidAssetsRubEquivKopecks: number;
  };
  drifts?: TreasuryDriftRow[];
  pnl: {
    allTimeGrossRevenueKopecks: number;
    allTimeNetProfitKopecks: number;
    last7dGrossRevenueKopecks: number;
    last7dNetProfitKopecks: number;
    crashHouseProfitKopecks: number;
    casesHouseProfitKopecks: number;
    referralExpenseKopecks: number;
  };
  fx: {
    tonUsd: number;
    tonRub: number;
    usdRub: number;
    gramRub: number;
  };
}

// START_CONTRACT: Treasury
//   PURPOSE: Render the 4-tab Platform Treasury control plane covering TON wallet reserves, Telegram Bot Stars 21-day hold, user balance coverage, ledger DRIFT reconciliation, and P&L.
//   INPUTS: none
//   OUTPUTS: React element
//   SIDE_EFFECTS: Queries GET /admin/treasury every 20s and posts corrective drift entries to POST /admin/treasury/reconcile-drift
//   LINKS: M-ADMIN
// END_CONTRACT: Treasury
export function Treasury() {
  // START_BLOCK_TREASURY
  const qc = useQueryClient();
  const toast = useToast();
  const { money } = useDisplayCurrency();
  const [activeTab, setActiveTab] = useState<TreasuryTab>('overview');
  const [reconcilingKey, setReconcilingKey] = useState<string | null>(null);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['admin-treasury'],
    queryFn: () => api.get<TreasuryResponse>('/admin/treasury'),
    refetchInterval: 20_000,
  });

  const reconcileMut = useMutation({
    mutationFn: async (input: { userId: string; currency: 'RUB' | 'GRAM' }) => {
      const key = `${input.userId}:${input.currency}`;
      setReconcilingKey(key);
      return api.post<{ reconciled: boolean; deltaApplied: number; currency: 'RUB' | 'GRAM' }>(
        '/admin/treasury/reconcile-drift',
        input,
      );
    },
    onSuccess: (res) => {
      if (res.reconciled) {
        const formatted =
          res.currency === 'GRAM'
            ? `${res.deltaApplied > 0 ? '+' : ''}${gram(res.deltaApplied)} GRAM`
            : `${res.deltaApplied > 0 ? '+' : ''}${rub(res.deltaApplied)} ₽`;
        toast(`Леджер выровнен (${formatted}) без изменения баланса профиля`);
      } else {
        toast('Расхождение уже отсутствует (0 DRIFT)');
      }
      void qc.invalidateQueries({ queryKey: ['admin-treasury'] });
      void qc.invalidateQueries({ queryKey: ['reconcile'] });
    },
    onError: (e) => {
      toast(e instanceof Error ? e.message : 'Ошибка выравнивания леджера');
    },
    onSettled: () => {
      setReconcilingKey(null);
    },
  });

  if (isLoading || !data) {
    return <div className="muted">Загрузка казначейства и резервов…</div>;
  }

  const botStars = data.botStars;
  const drifts = data.drifts ?? [];
  const driftCount = drifts.length > 0 ? drifts.length : data.liabilities.ledgerDriftUsersCount;

  const gramCovered = data.coverage?.gramCovered !== false;
  const gramSurplusMilli =
    data.coverage?.gramSurplusOrDeficitMilli ??
    Math.round(data.tonWallet.availableTon * 1000) - data.liabilities.totalUserBalanceMilliGram;

  const totalLiabilitiesRubEquivKopecks =
    data.coverage?.totalLiabilitiesRubEquivKopecks ??
    Math.round(
      data.liabilities.totalUserBalanceKopecks +
        (data.liabilities.totalUserBalanceMilliGram / 1000) * data.fx.gramRub * 100,
    );

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Казначейство и резервы (Treasury)</h1>
          <div className="page-subtitle">
            Резервы GRAM-кошелька (TON), холд Telegram Stars на боте (21 день), покрытие обязательств перед игроками, сверка леджера и P&amp;L
          </div>
        </div>
        <button
          type="button"
          className="btn ghost"
          disabled={isFetching}
          onClick={() => void refetch()}
        >
          <AdminIcon name="refresh" size={14} />
          <span>{isFetching ? 'Обновление…' : 'Обновить'}</span>
        </button>
      </div>

      {/* Top 4 KPI Cards (1-Star margin card removed) */}
      <div className="grid cols-4" style={{ marginBottom: 20 }}>
        <div className="kpi-card cyan">
          <div className="kpi-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <AdminIcon name="gram" size={14} />
            <span>GRAM-кошелёк казны (TON)</span>
          </div>
          <div className="kpi-value mono">{data.tonWallet.availableTon} GRAM</div>
          <div className="kpi-sub mono">
            ≈ {(data.tonWallet.availableTon * data.fx.tonRub).toFixed(0)} ₽ · курс 1 GRAM ={' '}
            {data.fx.tonRub} ₽
          </div>
        </div>

        <div className="kpi-card amber">
          <div className="kpi-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <AdminIcon name="star" size={14} />
            <span>Звёзды на боте Telegram</span>
          </div>
          <div className="kpi-value mono">{num(botStars?.totalStars ?? 0)} Stars</div>
          <div className="kpi-sub">
            {botStars?.available === false ? (
              <span className="muted">
                {botStars.error || 'Баланс Bot API временно недоступен'}
              </span>
            ) : (
              <span className="mono" style={{ fontSize: 11.5 }}>
                Готово к выводу: {num(botStars?.availableToWithdrawStars ?? 0)} · В холде 21д:{' '}
                {num(botStars?.heldStars ?? 0)}
              </span>
            )}
          </div>
        </div>

        <div className={`kpi-card ${gramCovered ? 'green' : 'pink'}`}>
          <div className="kpi-label">Счета пользователей vs Казна</div>
          <div className="kpi-value mono" style={{ fontSize: 19 }}>
            {rub(data.liabilities.totalUserBalanceKopecks)} ₽ +{' '}
            {gram(data.liabilities.totalUserBalanceMilliGram)} GRAM
          </div>
          <div className="kpi-sub">
            {gramCovered ? (
              <span style={{ color: 'var(--green)', fontWeight: 600 }}>
                Покрытие GRAM: в норме (+{gram(Math.abs(gramSurplusMilli))} GRAM)
              </span>
            ) : (
              <span style={{ color: 'var(--red)', fontWeight: 600 }}>
                Дефицит резерва: −{gram(Math.abs(gramSurplusMilli))} GRAM
              </span>
            )}
            {' · '}
            <span>Игроков с балансом: {num(data.liabilities.usersWithPositiveBalanceCount)}</span>
          </div>
        </div>

        <div className="kpi-card green">
          <div className="kpi-label">Чистая прибыль (All-Time)</div>
          <div className="kpi-value mono">{money(data.pnl.allTimeNetProfitKopecks)}</div>
          <div className="kpi-sub mono">
            За 7 дней: {money(data.pnl.last7dNetProfitKopecks)}
          </div>
        </div>
      </div>

      {/* 4 Horizontal Tabs */}
      <div className="tabs">
        <button
          type="button"
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Резервы и сверка счетов
        </button>
        <button
          type="button"
          className={`tab-btn ${activeTab === 'bot-stars' ? 'active' : ''}`}
          onClick={() => setActiveTab('bot-stars')}
        >
          Звёзды на боте (холд 21 день)
        </button>
        <button
          type="button"
          className={`tab-btn ${activeTab === 'drifts' ? 'active' : ''}`}
          onClick={() => setActiveTab('drifts')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <span>Сверка леджера (DRIFT)</span>
          {driftCount > 0 && (
            <span
              className="chip DELIVERY_FAILED mono"
              style={{ padding: '1px 6px', fontSize: 11 }}
            >
              {driftCount}
            </span>
          )}
        </button>
        <button
          type="button"
          className={`tab-btn ${activeTab === 'pnl' ? 'active' : ''}`}
          onClick={() => setActiveTab('pnl')}
        >
          Структура P&amp;L
        </button>
      </div>

      {/* Tab 1: Overview — Liabilities vs Reserves + Acquiring Infrastructure */}
      {activeTab === 'overview' && (
        <div className="grid cols-2">
          <div className="card">
            <h2 style={{ marginTop: 0 }}>Сверка балансов пользователей с резервами казны</h2>
            <div className="table-wrap">
              <table>
                <tbody>
                  <tr>
                    <td className="muted">Суммарно на RUB-счетах игроков</td>
                    <td className="mono" style={{ textAlign: 'right', fontWeight: 600 }}>
                      {rub(data.liabilities.totalUserBalanceKopecks)} ₽
                    </td>
                  </tr>
                  <tr>
                    <td className="muted">Суммарно на GRAM-счетах игроков</td>
                    <td className="mono" style={{ textAlign: 'right', fontWeight: 600 }}>
                      {gram(data.liabilities.totalUserBalanceMilliGram)} GRAM
                    </td>
                  </tr>
                  <tr>
                    <td className="muted">Суммарные обязательства в пересчёте на ₽</td>
                    <td className="mono" style={{ textAlign: 'right', fontWeight: 700 }}>
                      {rub(totalLiabilitiesRubEquivKopecks)} ₽
                    </td>
                  </tr>
                  <tr>
                    <td className="muted">На блокчейн-кошельке казны (TON)</td>
                    <td className="mono" style={{ textAlign: 'right', fontWeight: 600 }}>
                      {data.tonWallet.availableTon} GRAM (≈{' '}
                      {(data.tonWallet.availableTon * data.fx.tonRub).toFixed(0)} ₽)
                    </td>
                  </tr>
                  <tr>
                    <td className="muted">Статус покрытия GRAM-обязательств</td>
                    <td style={{ textAlign: 'right' }}>
                      {gramCovered ? (
                        <span className="chip ok mono">
                          <AdminIcon name="check" size={12} />В норме (+
                          {gram(Math.abs(gramSurplusMilli))} GRAM)
                        </span>
                      ) : (
                        <span className="chip DELIVERY_FAILED mono">
                          Дефицит резерва: −{gram(Math.abs(gramSurplusMilli))} GRAM
                        </span>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="muted">Сверка бухгалтерского леджера</td>
                    <td style={{ textAlign: 'right' }}>
                      {driftCount === 0 ? (
                        <span className="chip ok">
                          <AdminIcon name="check" size={12} />
                          Расхождений леджера: 0 — сходится
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="btn danger sm"
                          onClick={() => setActiveTab('drifts')}
                        >
                          <span>
                            Обнаружено расхождений DRIFT: {driftCount} → Открыть вкладку сверки
                          </span>
                        </button>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <h2 style={{ marginTop: 0 }}>Инфраструктура эквайринга и выплат</h2>
            <div className="table-wrap">
              <table>
                <tbody>
                  <tr>
                    <td className="muted">GRAM-кошелёк (сеть TON)</td>
                    <td className="mono" style={{ wordBreak: 'break-all' }}>
                      {data.tonWallet.address ?? 'Не настроен'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <span className={`chip ${data.tonWallet.healthy ? 'ok' : 'DELIVERY_FAILED'}`}>
                        {data.tonWallet.healthy ? 'ГОТОВ' : 'НЕДОСТУПЕН'}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td className="muted">Fragment API</td>
                    <td>
                      Режим: <strong>{data.fragment.mode.toUpperCase()}</strong> ({data.fragment.kycStatus})
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <span
                        className={`chip ${data.fragment.connected ? 'ok' : 'AWAITING_PAYMENT'}`}
                      >
                        {data.fragment.connected ? 'ПОДКЛЮЧЕН' : 'ОЖИДАНИЕ'}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td className="muted">Т-Банк СБП Watcher</td>
                    <td className="mono">{data.tbank.maskedPhone ?? '—'}</td>
                    <td style={{ textAlign: 'right' }}>
                      <span className={`chip ${data.tbank.circuitOpen ? 'DELIVERY_FAILED' : 'ok'}`}>
                        {data.tbank.state}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="muted" style={{ fontSize: 11.5, marginTop: 12 }}>
              Обновлено: {dt(data.tonWallet.updatedAt)}
            </div>
          </div>
        </div>
      )}

      {/* Tab 2: Bot Stars (21-Day Hold) */}
      {activeTab === 'bot-stars' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div
            className="card"
            style={{
              background: 'var(--bg-2)',
              borderColor: 'rgba(234, 184, 117, 0.25)',
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
              <AdminIcon name="star" size={16} />
              <span>Правило 21-дневного холда Telegram Stars</span>
            </div>
            <div className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
              Все входящие платежи в Telegram Stars (XTR), поступающие на баланс бота от пользователей,
              удерживаются платформой Telegram ровно <strong>21 сутки</strong> с момента транзакции.
              По истечении 21 дня звёзды автоматически переходят в пул доступных к выводу через Fragment.
            </div>
            {botStars?.available === false && (
              <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--amber)' }}>
                Статус Bot API: {botStars.error || 'Подключение к Telegram Bot API не активно'}
              </div>
            )}
          </div>

          <div className="grid cols-3">
            <div className="kpi-card amber">
              <div className="kpi-label">Всего на балансе бота</div>
              <div className="kpi-value mono">{num(botStars?.totalStars ?? 0)} Stars</div>
              <div className="kpi-sub">Общий остаток по методу getMyStarBalance</div>
            </div>

            <div className="kpi-card green">
              <div className="kpi-label">Доступно к выводу сейчас</div>
              <div className="kpi-value mono">
                {num(botStars?.availableToWithdrawStars ?? 0)} Stars
              </div>
              <div className="kpi-sub">Прошли 21-дневный период удержания</div>
            </div>

            <div className="kpi-card cyan">
              <div className="kpi-label">Заморожено в 21-дневном холде</div>
              <div className="kpi-value mono">{num(botStars?.heldStars ?? 0)} Stars</div>
              <div className="kpi-sub">
                Ближайшая разморозка:{' '}
                <span className="mono">
                  {botStars?.nextUnlockAt ? dt(botStars.nextUnlockAt) : 'нет активного холда'}
                </span>
              </div>
            </div>
          </div>

          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--hair)' }}>
              <h2 style={{ margin: 0 }}>Входящие транзакции Stars в 21-дневном холде</h2>
            </div>

            {(botStars?.recentHeldTransactions?.length ?? 0) > 0 ? (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>ID транзакции</th>
                      <th>От кого</th>
                      <th>Сумма Stars</th>
                      <th>Дата поступления</th>
                      <th>Дата разморозки (+21д)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(botStars?.recentHeldTransactions ?? []).map((tx) => {
                      const sender =
                        tx.fromLabel ||
                        (tx.fromUsername ? `@${tx.fromUsername}` : 'Платёж пользователя (XTR)');
                      return (
                        <tr key={tx.id}>
                          <td className="mono">{tx.id}</td>
                          <td>{sender}</td>
                          <td className="mono" style={{ fontWeight: 600 }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                              <AdminIcon name="star" size={13} />
                              <span>+{num(tx.stars)} Stars</span>
                            </span>
                          </td>
                          <td className="mono muted">{dt(tx.createdAt)}</td>
                          <td className="mono">{dt(tx.unlocksAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="muted" style={{ padding: '26px 20px', textAlign: 'center' }}>
                {botStars?.available === false
                  ? 'Данные транзакций Telegram Stars недоступны: проверьте рабочий BOT_TOKEN и доступ к api.telegram.org.'
                  : 'Входящих платежей Telegram Stars с активным 21-дневным холдом сейчас нет.'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab 3: Ledger Reconciliation (DRIFT) */}
      {activeTab === 'drifts' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div
            className="card"
            style={{
              background: 'var(--bg-2)',
              borderColor: 'rgba(130, 148, 255, 0.25)',
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
              <AdminIcon name="shield" size={15} />
              <span>Что такое DRIFT?</span>
            </div>
            <div className="muted" style={{ fontSize: 13, lineHeight: 1.55 }}>
              Запись DRIFT означает, что текущий баланс в карточке пользователя (<code>users.balance</code>)
              не совпадает с суммой всех его операций в бухгалтерском журнале (<code>transactions</code>).
              Чаще всего это возникает после старых ручных правок БД мимо леджера. Кнопка{' '}
              <strong>«Выровнять леджер»</strong> добавляет корректирующую проводку аудита, чтобы{' '}
              <code>ledgerSum === storedBalance</code> без изменения доступного баланса самого игрока.
            </div>
          </div>

          {drifts.length === 0 ? (
            <div
              className="card"
              style={{
                borderColor: 'rgba(104, 203, 160, 0.32)',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <span className="chip ok">
                <AdminIcon name="check" size={14} />0 DRIFT
              </span>
              <span style={{ fontWeight: 500 }}>
                Расхождений между балансами профилей и журналом транзакций нет (0 DRIFT)
              </span>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Пользователь</th>
                      <th>Валюта</th>
                      <th>Баланс в профиле</th>
                      <th>Сумма по леджеру</th>
                      <th>Дельта расхождения</th>
                      <th style={{ textAlign: 'right' }}>Действие</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drifts.map((d) => {
                      const delta = d.driftAmount ?? d.storedBalance - d.ledgerSum;
                      const isGram = d.currency === 'GRAM';
                      const fmtUnit = (v: number) =>
                        isGram ? `${gram(v)} GRAM` : `${rub(v)} ₽`;
                      const signedDelta = `${delta > 0 ? '+' : '−'}${fmtUnit(Math.abs(delta))}`;
                      const searchParam = d.username || String(d.telegramId);
                      const rowKey = `${d.userId}:${d.currency}`;
                      const rowBusy = reconcileMut.isPending && reconcilingKey === rowKey;

                      return (
                        <tr key={rowKey}>
                          <td>
                            <Link
                              to={`/users?q=${encodeURIComponent(searchParam)}${d.userId ? `&userId=${encodeURIComponent(d.userId)}` : ''}`}
                              style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}
                            >
                              <span style={{ fontWeight: 600, color: '#b4c2ff' }}>
                                {d.displayName || (d.username ? `@${d.username}` : `TG #${d.telegramId}`)}
                              </span>
                              <span className="muted mono" style={{ fontSize: 11.5 }}>
                                {d.username ? `@${d.username} · ` : ''}TG #{d.telegramId}
                              </span>
                            </Link>
                          </td>
                          <td>
                            <span className={`chip ${isGram ? 'info' : 'AWAITING_PAYMENT'} mono`}>
                              {d.currency}
                            </span>
                          </td>
                          <td className="mono">{fmtUnit(d.storedBalance)}</td>
                          <td className="mono">{fmtUnit(d.ledgerSum)}</td>
                          <td
                            className="mono"
                            style={{
                              fontWeight: 700,
                              color: delta > 0 ? 'var(--amber)' : 'var(--red)',
                            }}
                          >
                            {signedDelta}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <button
                              type="button"
                              className="btn sm"
                              disabled={reconcileMut.isPending}
                              onClick={() =>
                                reconcileMut.mutate({
                                  userId: d.userId,
                                  currency: d.currency,
                                })
                              }
                            >
                              <AdminIcon name="check" size={13} />
                              <span>{rowBusy ? 'Выравнивание…' : 'Выровнять леджер'}</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab 4: Platform P&L Breakdown */}
      {activeTab === 'pnl' && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Структура P&amp;L платформы</h2>
          <div className="table-wrap">
            <table>
              <tbody>
                <tr>
                  <td className="muted">Валовый оборот доставленных заказов (All-Time)</td>
                  <td className="mono" style={{ textAlign: 'right', fontWeight: 700 }}>
                    {rub(data.pnl.allTimeGrossRevenueKopecks)} ₽
                  </td>
                </tr>
                <tr>
                  <td className="muted">Валовый оборот за последние 7 дней</td>
                  <td className="mono" style={{ textAlign: 'right' }}>
                    {rub(data.pnl.last7dGrossRevenueKopecks)} ₽
                  </td>
                </tr>
                <tr>
                  <td className="muted">Профит Crash</td>
                  <td className="mono" style={{ textAlign: 'right' }}>
                    {rub(data.pnl.crashHouseProfitKopecks)} ₽
                  </td>
                </tr>
                <tr>
                  <td className="muted">Профит Кейсов</td>
                  <td className="mono" style={{ textAlign: 'right' }}>
                    {rub(data.pnl.casesHouseProfitKopecks)} ₽
                  </td>
                </tr>
                <tr>
                  <td className="muted">Выплачено реферальных наград</td>
                  <td className="mono" style={{ textAlign: 'right', color: 'var(--amber)' }}>
                    −{rub(data.pnl.referralExpenseKopecks)} ₽
                  </td>
                </tr>
                <tr>
                  <td className="muted">Чистая операционная прибыль (7 дней)</td>
                  <td className="mono" style={{ textAlign: 'right', color: 'var(--green)' }}>
                    {rub(data.pnl.last7dNetProfitKopecks)} ₽
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Итого чистая операционная прибыль</strong>
                  </td>
                  <td
                    className="mono"
                    style={{
                      textAlign: 'right',
                      fontWeight: 700,
                      fontSize: 15,
                      color: 'var(--green)',
                    }}
                  >
                    {rub(data.pnl.allTimeNetProfitKopecks)} ₽
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
  // END_BLOCK_TREASURY
}
